import React from 'react';
import { Stack } from '@cp/ds/src/components/stack';
import { Trademark } from '@cp/ds/src/components/trademark';
import loadable from '@loadable/component';
import { useTheme } from '@cp/ds/src/hooks/useTheme';
import { useMediaQuery } from '@mui/material';
import { Badge } from '@cp/ds/src/components/badge';
import { HeartIcon } from '@cp/ds/src/icons';
import { ClientWebPage } from '@common/model/meta/clientWebPage';
import { usePathGenerator } from '@cp/entities/clientWebRoutes';
import { RoutedLink as Link } from 'src/shared/ui/link';
import { CitiesListLink, CitySelectionButton } from 'src/features/location';
import { EButtonName } from 'src/shared/tracking';
import { LoginButton } from 'src/features/auth';
import { useFavorites } from 'src/features/client/current/favorite';
import { HeaderFeature, useSyncDimensions } from '../model';
import { HeaderContentProps } from './model';

const NavigateToCatalogLink = loadable(() => import('src/features/catalog/ui/navigateLink').then((module) => module.default));
const ClientDashboard = loadable(() => import('src/widgets/client/current/dashboard'));
const ClientMenu = loadable(() => import('src/widgets/client/current/menu'));

export const HeaderContentDesktop: React.FC<HeaderContentProps> = ({
  features,
  trademarkSize,
  onLogoClick,
  clientProfile,
  rightBlock,
  leftBlock,
  containerRef,
}) => {
  const theme = useTheme();
  const favorites = useFavorites();
  const generateAppPath = usePathGenerator();
  const height = useSyncDimensions(containerRef);
  const isXxlScreen = useMediaQuery(theme.breakpoints.up('xxl'));

  return (
    <>
      <Stack alignItems="center" direction="row">
        <Link disableHover onClick={onLogoClick} underline={false}>
          {/* eslint-disable-next-line @typescript-eslint/no-magic-numbers */}
          <Trademark size={trademarkSize ?? (isXxlScreen ? 3.164375 : 2.125)} />
        </Link>
        {features[HeaderFeature.CitiesList] ? <CitiesListLink /> : null}
        {features[HeaderFeature.CitySelection] ? <CitySelectionButton /> : null}
        {features[HeaderFeature.NavigateToCatalog] ? <NavigateToCatalogLink sx={{ ml: 6.25 }} /> : null}
        {leftBlock}
      </Stack>
      {rightBlock}
      {features[HeaderFeature.ClientMenu] && clientProfile ? (
        <ClientMenu absoluteTopPosition={height}>
          {({ ref, isMenuOpened, toggleMenu }) => (
            <ClientDashboard
              hasFavorites={features[HeaderFeature.Favorites]}
              isProfileButtonActive={isMenuOpened}
              onProfileButtonClick={toggleMenu}
              ref={ref as React.Ref<HTMLDivElement> | undefined}
            />
          )}
        </ClientMenu>
      ) : null}
      {!clientProfile && (features[HeaderFeature.LoginButton] || features[HeaderFeature.Favorites]) ? (
        <Stack alignItems="center" direction="row" spacing={3}>
          {features[HeaderFeature.Favorites] && (
            <Link href={generateAppPath({ page: ClientWebPage.Favorites })} underline={false}>
              <Badge badgeContent={favorites.length || 0}>
                <HeartIcon sx={{ color: 'brand gray', ml: 1, '&:hover': { color: 'brand pink' } }} />
              </Badge>
            </Link>
          )}
          {features[HeaderFeature.LoginButton] && (
            <LoginButton className={EButtonName.Login} size="small" variant="secondary">
              Войти
            </LoginButton>
          )}
        </Stack>
      ) : null}
    </>
  );
};
